import React, { useState } from 'react';
import { X, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const typeConfig = {
  suggestion_approved: { label: 'Approved', color: '#00d4ff', bg: 'rgba(0,212,255,0.08)', border: 'rgba(0,212,255,0.25)' },
  suggestion_denied: { label: 'Denied', color: '#ff6b6b', bg: 'rgba(255,107,107,0.08)', border: 'rgba(255,107,107,0.25)' },
  track_share: { label: 'Track Shared', color: '#a78bfa', bg: 'rgba(167,139,250,0.08)', border: 'rgba(167,139,250,0.25)' },
  new_follower: { label: 'New Follower', color: '#34d399', bg: 'rgba(52,211,153,0.08)', border: 'rgba(52,211,153,0.25)' },
  friend_request: { label: 'Friend Request', color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.25)' },
  demo_deadline_reminder: { label: 'Demo Reminder', color: '#f97316', bg: 'rgba(249,115,22,0.08)', border: 'rgba(249,115,22,0.25)' },
  edit_drop: { label: 'Edit Dropped', color: '#f7d55c', bg: 'rgba(247,213,92,0.08)', border: 'rgba(247,213,92,0.3)' },
  id_hunter_rank: { label: 'ID Hunter', color: '#ff2d78', bg: 'rgba(255,45,120,0.08)', border: 'rgba(255,45,120,0.3)' },
  chat_invite: { label: 'Chat Invite', color: '#22d3ee', bg: 'rgba(34,211,238,0.08)', border: 'rgba(34,211,238,0.3)' },
  id_correction: { label: 'ID Suggestion', color: '#00e5ff', bg: 'rgba(0,229,255,0.08)', border: 'rgba(0,229,255,0.3)' },
};

const defaultType = { label: 'Notification', color: '#94a3b8', bg: 'rgba(148,163,184,0.08)', border: 'rgba(148,163,184,0.25)' };

const FILTER_CHIPS = [
  { id: 'all', label: 'All' },
  { id: 'id', label: 'ID Alerts', types: ['edit_drop', 'id_hunter_rank', 'id_correction'] },
  { id: 'followers', label: 'Followers', types: ['new_follower'] },
  { id: 'events', label: 'Events', types: ['event_planner', 'event_reminder', 'event_update'] },
  { id: 'shares', label: 'Shares', types: ['track_share'] },
  { id: 'other', label: 'Other' },
];
const CHIP_TYPES = FILTER_CHIPS.flatMap(c => c.types || []);

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function NotificationsPanel({ isOpen, onClose, notifications, onRefresh, userId }) {
  const [markingAll, setMarkingAll] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [followedBack, setFollowedBack] = useState({});

  if (!isOpen) return null;

  const unreadCount = notifications.filter(n => !n.read).length;

  const chipMatches = (n, chip) => {
    if (chip.id === 'all') return true;
    if (chip.id === 'other') return !CHIP_TYPES.includes(n.type);
    return chip.types.includes(n.type);
  };
  const activeChip = FILTER_CHIPS.find(c => c.id === activeFilter) || FILTER_CHIPS[0];
  const filteredNotifications = notifications.filter(n => chipMatches(n, activeChip));

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('beatseek_token') || ''}`
  });

  const followBack = async (n) => {
    if (!userId || !n.from_user_id) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/users/${userId}/follow`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ followed_user_id: n.from_user_id })
      });
      if (r.ok || r.status === 400) {
        // 400 = already following — treat as success
        setFollowedBack(prev => ({ ...prev, [n.from_user_id]: true }));
      }
    } catch (e) { console.error('Follow back error:', e); }
  };

  const deleteNotification = async (notifId) => {
    try {
      await fetch(`${BACKEND_URL}/api/notifications/${notifId}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      onRefresh();
    } catch (e) { console.error('Delete notification error:', e); }
  };

  const clearAll = async () => {
    if (!userId) return;
    if (!confirmClear) {
      setConfirmClear(true);
      setTimeout(() => setConfirmClear(false), 4000);
      return;
    }
    setClearingAll(true);
    try {
      await fetch(`${BACKEND_URL}/api/users/${userId}/notifications/clear-all`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      onRefresh();
    } catch (e) { console.error('Clear all error:', e); }
    setClearingAll(false);
    setConfirmClear(false);
  };

  const markAsRead = async (notifId) => {
    try {
      const token = localStorage.getItem('beatseek_token');
      await fetch(`${BACKEND_URL}/api/notifications/${notifId}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      onRefresh();
    } catch (e) { console.error('Mark read error:', e); }
  };

  const markAllRead = async () => {
    if (!userId) return;
    setMarkingAll(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      await fetch(`${BACKEND_URL}/api/users/${userId}/notifications/read-all`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      onRefresh();
    } catch (e) { console.error('Mark all read error:', e); }
    setMarkingAll(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="notifications-panel-overlay">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-lg max-h-[80vh] flex flex-col rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0f1724 0%, #0a0e1a 100%)',
          border: '1px solid rgba(0,212,255,0.15)',
          boxShadow: '0 0 40px rgba(0,212,255,0.08)',
        }}
        data-testid="notifications-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'rgba(0,212,255,0.12)' }}>
          <div className="flex items-center gap-3">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00d4ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <h3 className="text-white font-semibold text-base">Notifications</h3>
            {unreadCount > 0 && (
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,212,255,0.15)', color: '#00d4ff' }}>
                {unreadCount} new
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                disabled={markingAll}
                className="text-[11px] px-2.5 py-1 rounded-md transition-colors"
                style={{ background: 'rgba(0,212,255,0.1)', color: '#00d4ff', border: '1px solid rgba(0,212,255,0.2)' }}
                data-testid="mark-all-read-btn"
              >
                {markingAll ? 'Marking...' : 'Mark all read'}
              </button>
            )}
            {notifications.length > 0 && (
              <button
                onClick={clearAll}
                disabled={clearingAll}
                className="text-[11px] px-2.5 py-1 rounded-md transition-colors"
                style={{
                  background: confirmClear ? 'rgba(255,51,51,0.2)' : 'rgba(255,51,51,0.08)',
                  color: '#ff6b6b',
                  border: '1px solid rgba(255,51,51,0.3)'
                }}
                data-testid="clear-all-notifications-btn"
              >
                {clearingAll ? 'Clearing...' : (confirmClear ? 'Confirm?' : 'Clear all')}
              </button>
            )}
            <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors" data-testid="notifications-close-btn">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Filter Chips */}
        <div className="flex items-center gap-2 px-5 py-2.5 border-b flex-wrap" style={{ borderColor: 'rgba(0,212,255,0.08)' }}>
          {FILTER_CHIPS.map(chip => {
            const count = notifications.filter(n => chipMatches(n, chip)).length;
            const active = activeFilter === chip.id;
            return (
              <button
                key={chip.id}
                onClick={() => setActiveFilter(chip.id)}
                className="text-[11px] font-semibold px-2.5 py-1 rounded-full transition-colors"
                style={{
                  background: active ? 'rgba(0,212,255,0.18)' : 'rgba(255,255,255,0.04)',
                  color: active ? '#00d4ff' : '#94a3b8',
                  border: active ? '1px solid rgba(0,212,255,0.5)' : '1px solid rgba(255,255,255,0.08)'
                }}
                data-testid={`notif-filter-${chip.id}`}
              >
                {chip.label}{count > 0 && ` · ${count}`}
              </button>
            );
          })}
        </div>

        {/* Notification List */}
        <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
          {filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="rgba(148,163,184,0.3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              <p className="text-gray-500 text-sm mt-3">
                {notifications.length === 0 ? 'No notifications yet' : `No ${activeChip.label.toLowerCase()} notifications`}
              </p>
              <p className="text-gray-600 text-xs mt-1">You'll see edit drops, ID Hunter ranks, follows, and more here.</p>
            </div>
          ) : (
            <div>
              {filteredNotifications.map((n, idx) => {
                const config = typeConfig[n.type] || defaultType;
                return (
                  <div
                    key={n.id || idx}
                    className="flex items-start gap-3 px-5 py-3.5 transition-colors cursor-pointer"
                    style={{
                      background: n.read ? 'transparent' : config.bg,
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                    }}
                    onClick={() => !n.read && markAsRead(n.id)}
                    data-testid={`notification-item-${idx}`}
                  >
                    {/* Unread dot */}
                    <div className="pt-1.5 shrink-0" style={{ width: '8px' }}>
                      {!n.read && (
                        <div className="w-2 h-2 rounded-full" style={{ background: config.color }} />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span
                          className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{ background: config.bg, color: config.color, border: `1px solid ${config.border}` }}
                        >
                          {config.label}
                        </span>
                        <span className="text-[11px] text-gray-600">{timeAgo(n.created_at)}</span>
                      </div>
                      {n.title && <p className="text-sm font-semibold text-white leading-snug mt-1" data-testid={`notification-title-${idx}`}>{n.title}</p>}
                      <p className="text-sm text-gray-300 leading-snug mt-1">{n.data?.link ? n.message.replace(n.data.link, '').replace(/Grab it:\s*$/, '').trim() : n.message}</p>
                      {n.data?.link && (
                        <a
                          href={n.data.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => { e.stopPropagation(); if (!n.read) markAsRead(n.id); }}
                          className="inline-block text-[11px] font-bold px-3 py-1 rounded-full mt-2 transition-transform hover:scale-105"
                          style={{ background: '#f7d55c', color: '#000', textDecoration: 'none' }}
                          data-testid={`notification-link-${idx}`}
                        >
                          Get it ↗
                        </a>
                      )}
                      {n.from_user_name && n.from_user_name !== 'system' && n.from_user_name !== 'BEATSEEK' && (
                        <p className="text-[11px] text-gray-600 mt-1">from {n.from_user_name}</p>
                      )}
                      {n.type === 'new_follower' && n.from_user_id && (
                        (n.is_following_back || followedBack[n.from_user_id]) ? (
                          <span
                            className="inline-block text-[11px] font-semibold px-2.5 py-1 rounded-md mt-2"
                            style={{ background: 'rgba(52,211,153,0.12)', color: '#34d399', border: '1px solid rgba(52,211,153,0.35)' }}
                            data-testid={`following-back-badge-${idx}`}
                          >
                            ✓ Following
                          </span>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); followBack(n); }}
                            className="inline-block text-[11px] font-semibold px-2.5 py-1 rounded-md mt-2 transition-colors hover:brightness-125"
                            style={{ background: 'rgba(0,212,255,0.12)', color: '#00d4ff', border: '1px solid rgba(0,212,255,0.4)', cursor: 'pointer' }}
                            data-testid={`follow-back-btn-${idx}`}
                          >
                            + Follow back
                          </button>
                        )
                      )}
                      {n.type === 'chat_invite' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.dispatchEvent(new CustomEvent('openCommunityChat', { detail: { room: n.data?.room_name, room_id: n.data?.room_id } }));
                            onClose();
                          }}
                          className="inline-block text-[11px] font-semibold px-2.5 py-1 rounded-md mt-2 transition-colors hover:brightness-125"
                          style={{ background: 'rgba(34,211,238,0.12)', color: '#22d3ee', border: '1px solid rgba(34,211,238,0.4)', cursor: 'pointer' }}
                          data-testid={`join-room-btn-${idx}`}
                        >
                          # Join the room
                        </button>
                      )}
                    </div>

                    {/* Delete notification */}
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteNotification(n.id); }}
                      className="shrink-0 pt-1 text-gray-600 hover:text-red-400 transition-colors"
                      title="Delete notification"
                      data-testid={`delete-notification-btn-${idx}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}